<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

// One LLM call within a DET step or subroutine. Owns its own expand state and
// its own raw-vs-prompt body view. The call shape is open (backend-authored;
// see DETLLMCallSchema), so it's typed loosely at the boundary.
const props = defineProps<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    call: Record<string, any>;
}>();

const expanded = ref(false);
// Body view: false (default) = raw Input/Output; true = serialized
// PromptInput/PromptOutput. Only offered when the call has prompt-framework
// fields; the two views are mutually exclusive.
const showPromptView = ref(false);

const hasPromptFields = computed(
    () => props.call.prompt_input != null || props.call.prompt_output != null
);

function formatDuration(seconds: number | null | undefined) {
    if (seconds == null) return '-';
    return seconds < 1 ? `${(seconds * 1000).toFixed(0)}ms` : `${seconds.toFixed(1)}s`;
}
function formatTokens(n: number | null | undefined) {
    if (n == null) return '-';
    return n.toLocaleString();
}
</script>

<template>
    <div class="det-debug-llm-call">
        <div class="det-debug-llm-header" @click="expanded = !expanded">
            <span class="det-debug-expand-icon material-icons">
                {{ expanded ? 'expand_more' : 'chevron_right' }}
            </span>
            <span class="det-debug-llm-meta">
                <span class="det-debug-llm-label">{{ call.label || t('debug.unnamed') }}</span>
                <span class="det-debug-llm-model">{{ call.model_name || t('debug.unknown') }}</span>
                <span class="det-debug-llm-dur">{{ formatDuration(call.duration_seconds) }}</span>
                <span class="det-debug-llm-tokens">{{
                    t('debug.tokensInOut', {
                        in: formatTokens(call.input_tokens),
                        out: formatTokens(call.output_tokens),
                    })
                }}</span>
                <span v-if="call.prompt_identifier" class="det-debug-llm-prompt-id">{{
                    call.prompt_identifier
                }}</span>
            </span>
        </div>

        <div v-if="expanded" class="det-debug-llm-body">
            <!-- Raw / Prompt toggle — only when the call carries prompt fields -->
            <div v-if="hasPromptFields" class="det-debug-view-toggle det-debug-body-toggle">
                <button :class="{ active: !showPromptView }" @click="showPromptView = false">
                    {{ t('debug.rawView') }}
                </button>
                <button :class="{ active: showPromptView }" @click="showPromptView = true">
                    {{ t('debug.promptView') }}
                </button>
            </div>

            <div v-if="hasPromptFields && showPromptView" class="det-debug-side-by-side">
                <div class="det-debug-side-col">
                    <div class="det-debug-side-label">{{ t('debug.promptInput') }}</div>
                    <pre class="det-debug-mono det-debug-llm-content">{{ call.prompt_input }}</pre>
                </div>
                <div class="det-debug-side-col">
                    <div class="det-debug-side-label">{{ t('debug.promptOutput') }}</div>
                    <pre class="det-debug-mono det-debug-llm-content">{{ call.prompt_output }}</pre>
                </div>
            </div>

            <div v-else class="det-debug-side-by-side">
                <div class="det-debug-side-col">
                    <div class="det-debug-side-label">{{ t('debug.input') }}</div>
                    <pre class="det-debug-mono det-debug-llm-content">{{ call.input_text }}</pre>
                </div>
                <div class="det-debug-side-col">
                    <div class="det-debug-side-label">{{ t('debug.output') }}</div>
                    <pre class="det-debug-mono det-debug-llm-content">{{ call.output_text }}</pre>
                </div>
            </div>
        </div>
    </div>
</template>
